import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Download, FileText, ChevronDown } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function SmartDoorPage() {
  const {
    t
  } = useLanguage();
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const advantages = [t('door.adv1'), t('door.adv2'), t('door.adv3'), t('door.adv4'), t('door.adv5'), t('door.adv6')];
  return <div className="w-full bg-white pb-32" data-id="element-581">
      <div className="bg-corporate-dark text-white py-24 md:py-40 px-4 relative overflow-hidden" data-id="element-582">
        <div className="absolute inset-0 opacity-20" data-id="element-583">
          <img src="https://cdn.magicpatterns.com/uploads/qdv3TgAR2TPZ57JkpD3a3e/SmartDoor_(puertas.png" alt="Background" className="w-full h-full object-cover" data-id="element-584" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10 mt-10" data-id="element-585">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} data-id="element-586">
            <span className="text-accent-beige font-medium tracking-[0.2em] uppercase text-sm mb-6 block" data-id="element-587">
              {t('prod.line')}
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold mb-6 tracking-tight" data-id="element-588">
              SmartDoor
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-2xl font-light leading-relaxed" data-id="element-589">
              {t('door.hero.desc')}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 lg:mt-28" data-id="element-590">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20" data-id="element-591">
          <motion.div initial={{
          opacity: 0,
          x: -40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2,
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} data-id="element-592">
            <h2 className="text-4xl font-heading font-bold text-corporate-dark mb-8 tracking-tight" data-id="element-593">
              {t('door.sec.title')}
            </h2>
            <div className="text-lg text-gray-600 font-light leading-relaxed space-y-6 mb-12" data-id="element-594">
              <p data-id="element-595">{t('door.sec.p1')}</p>
              <p data-id="element-596">{t('door.sec.p2')}</p>
            </div>

            <h3 className="text-2xl font-heading font-bold text-corporate-dark mb-8 tracking-tight" data-id="element-597">
              {t('prod.adv.title')}
            </h3>
            <ul className="space-y-5 mb-12" data-id="element-598">
              {advantages.map((adv, idx) => <li key={idx} className="flex items-start" data-id="element-599">
                  <CheckCircle2 className="h-6 w-6 text-corporate-blue mr-4 flex-shrink-0 mt-0.5" data-id="element-600" />
                  <span className="text-gray-700 text-lg font-light" data-id="element-601">
                    {adv}
                  </span>
                </li>)}
            </ul>

            <a href="#documentacion-tecnica" onClick={e => {
            e.preventDefault();
            document.getElementById('documentacion-tecnica')?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }} className="inline-flex items-center px-8 py-4 bg-neutral-grey text-corporate-dark font-medium rounded-md hover:bg-gray-200 hover:scale-[1.02] transition-all duration-500 ease-premium" data-id="element-602">
              <Download className="mr-3 h-5 w-5" data-id="element-603" />
              {t('prod.dl.btn')}
            </a>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3,
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="space-y-8" data-id="element-604">
            <div className="h-96 bg-gray-100 rounded-sm overflow-hidden group" data-id="element-605">
              <img src="https://cdn.magicpatterns.com/uploads/qdv3TgAR2TPZ57JkpD3a3e/SmartDoor_(puertas.png" alt="Aplicación SmartDoor" className="w-full h-full object-cover transition-transform duration-700 ease-premium group-hover:scale-105" data-id="element-606" />
            </div>
            <div className="grid grid-cols-2 gap-8" data-id="element-607">
              <div className="bg-neutral-grey p-8 rounded-sm hover:shadow-md transition-all duration-500 ease-premium" data-id="element-608">
                <h4 className="text-xl font-heading font-bold text-corporate-dark mb-3 tracking-tight" data-id="element-609">
                  {t('door.app1.title')}
                </h4>
                <p className="text-base text-gray-600 font-light leading-relaxed" data-id="element-610">
                  {t('door.app1.desc')}
                </p>
              </div>
              <div className="bg-neutral-grey p-8 rounded-sm hover:shadow-md transition-all duration-500 ease-premium" data-id="element-611">
                <h4 className="text-xl font-heading font-bold text-corporate-dark mb-3 tracking-tight" data-id="element-612">
                  {t('door.app2.title')}
                </h4>
                <p className="text-base text-gray-600 font-light leading-relaxed" data-id="element-613">
                  {t('door.app2.desc')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Documentación Técnica */}
        <motion.div id="documentacion-tecnica" initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5,
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="mt-24 lg:mt-32 scroll-mt-24" data-id="element-614">
          <h3 className="text-3xl font-heading font-bold text-corporate-dark mb-3 tracking-tight" data-id="element-615">
            {t('prod.docs.title')}
          </h3>
          <p className="text-lg text-gray-500 font-light mb-12" data-id="element-616">
            {t('prod.docs.door.desc')}
          </p>

          {[{
          id: 'puertas',
          title: 'Puertas WPC',
          description: 'Catálogos y fichas técnicas del sistema de puertas WPC SmartDoor.',
          docs: [{
            name: 'Puertas WPC – Catálogo General',
            url: 'https://drive.google.com/file/d/1cY9ruqc99fFkPyl7Yoo7qjkQmL7LZpUC/view?usp=sharing'
          }, {
            name: 'Puertas WPC – Certificado de Garantía',
            url: 'https://drive.google.com/file/d/1i_yf0-LupXjGFo_bs-V6KO-n4YY84ffK/view?usp=sharing'
          }, {
            name: 'Puertas WPC – Ficha Técnica General',
            url: 'https://drive.google.com/file/d/1I0cIDKbb841zcpWWdAVyT-rb9fJGTOY1/view?usp=sharing'
          }, {
            name: 'Puertas WPC – Ficha Técnica Detallada',
            url: 'https://drive.google.com/file/d/1vdvb8WRS6ywY7bgTg3OIxawz9TelwCRG/view?usp=sharing'
          }]
        }, {
          id: 'blindadas',
          title: 'Puertas Blindadas',
          description: 'Catálogos y fichas técnicas del sistema de puertas blindadas SmartDoor.',
          docs: [{
            name: 'Puertas Blindadas – Catálogo General',
            url: 'https://drive.google.com/file/d/1bSKeWyOCwVZHoWeGR5R46EcPhcSoOytN/view?usp=sharing'
          }, {
            name: 'Puertas Blindadas – Ficha Técnica',
            url: 'https://drive.google.com/file/d/1iekhDIGmVVDsbNcG3q9oOpFhRQtcnTKj/view?usp=sharing'
          }]
        }].map(category => <div key={category.id} className="bg-white border border-gray-100 rounded-sm shadow-md overflow-hidden mb-4" data-id="element-617">
              <button onClick={() => setOpenAccordion(openAccordion === category.id ? null : category.id)} className="w-full flex items-center justify-between px-8 py-6 text-left hover:bg-neutral-grey transition-colors duration-300" data-id="element-618">
                <div data-id="element-619">
                  <h4 className="text-xl font-heading font-bold text-corporate-dark tracking-tight" data-id="element-620">
                    {category.title}
                  </h4>
                  <p className="text-sm text-gray-500 font-light mt-1" data-id="element-621">
                    {category.description}
                  </p>
                </div>
                <ChevronDown className={`h-5 w-5 text-corporate-blue flex-shrink-0 ml-4 transition-transform duration-300 ${openAccordion === category.id ? 'rotate-180' : ''}`} data-id="element-622" />
              </button>
              <AnimatePresence initial={false} data-id="element-623">
                {openAccordion === category.id && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.35,
              ease: [0.25, 0.1, 0.25, 1]
            }} className="overflow-hidden" data-id="element-624">
                    <div className="divide-y divide-gray-100 border-t border-gray-100" data-id="element-625">
                      {category.docs.map((doc, idx) => <div key={idx} className="flex flex-col sm:flex-row sm:items-center justify-between px-8 py-5 hover:bg-neutral-grey transition-colors duration-300" data-id="element-626">
                          <div className="flex items-center mb-4 sm:mb-0" data-id="element-627">
                            <FileText className="h-5 w-5 text-corporate-blue mr-4 flex-shrink-0" data-id="element-628" />
                            <span className="text-base text-gray-800 font-medium" data-id="element-629">
                              {doc.name}
                            </span>
                          </div>
                          <a href={doc.url} target="_blank" rel="noopener noreferrer" download className="inline-flex items-center justify-center px-5 py-2.5 border border-corporate-blue text-corporate-blue hover:bg-corporate-blue hover:text-white transition-all duration-500 ease-premium rounded-md text-sm font-medium w-full sm:w-auto hover:scale-[1.02]" data-id="element-630">
                            <Download className="mr-2 h-4 w-4" data-id="element-631" />
                            {t('prod.docs.download')}
                          </a>
                        </div>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </motion.div>
      </div>
    </div>;
}